import React from 'react';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/modern/components/ui/dropdown-menu';
import {
  canArchive,
  canDelete,
  canShare,
  canCopyToBoard,
} from '../contextMenuUtils';
import { getEntityShareLink } from '@/other/utils/otherUtils';
import { showShareUrlOptions, showUnlinkRelatedEntityPrompt } from '@/other/utils/utils';
import { DEEP_LINK_URL_KEYS } from '@/other/constants/OtherKeys';
import { Client } from '@/bryntum-lib/models/Client';
import { toast } from 'sonner';
import { useConfirm } from '@omit/react-confirm-dialog';
import { useSwivuBryntumContext } from '@/react/hooks/useSwivuBryntumContext';
import { Model } from '@bryntum/schedulerpro';

interface ClientContextMenuProps {
  record: Client;
  onClose?: () => void;
  onItemClick?: (item: { name: string; }) => void;
}

const ClientContextMenu: React.FC<ClientContextMenuProps> = ({ record, onClose, onItemClick }) => {
  const context = useSwivuBryntumContext();
  const { user, project, selectedBoard, boardManager, viewManager } = context;
  const confirm = useConfirm();

  const isDemoUser = user.isDemoUser;

  // Permission checks
  const showArchive = canArchive(user, record);
  const showDelete = canDelete(user, record);
  const showShare = canShare(user, isDemoUser, record);
  const showCopyTo = canCopyToBoard(user, isDemoUser, selectedBoard, boardManager?.boardNameStore);

  // Action handlers
  const handleArchive = async () => {
    if (!record.archived && record.swivuUnlinkPromptConfig) {
      const isArchived = await showUnlinkRelatedEntityPrompt(record.swivuUnlinkPromptConfig);
      if (isArchived) {
        onClose?.();
      }
    } else {
      record.archived = !record.archived;
      onClose?.();
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete client',
      description: `Are you sure you want to delete "${record?.name || 'this client'}"? This action cannot be undone.`,
      confirmButton: {
        variant: 'destructive',
        title: 'Delete client'
      },
      cancelButton: {
        variant: 'ghost'
      }
    });

    if (!confirmed) return;

    try {
      const recordStore = project?.clientsStore;
      if (recordStore) {
        recordStore.remove(record as unknown as Model);
        toast.success('Client deleted successfully');
        onClose?.();

        if (viewManager) {
          viewManager.goToPreviousView();
        }
      }
    } catch (error) {
      console.error('Failed to delete client:', error);
      toast.error('Something went wrong! Unable to delete client');
    }
  };

  const handleShare = () => {
    const shareLink = getEntityShareLink({
      record,
      context,
      editorTabs: 'general',
      entityType: DEEP_LINK_URL_KEYS.client,
      entityDetail: record,
      otherKeys: undefined,
    });
    showShareUrlOptions(shareLink);
    onClose?.();
  };

  const handleCopyTo = () => {
    onItemClick?.({ name: 'copyTo' });
  };

  return (
    <>
      {/* Share */}
      <DropdownMenuItem onClick={handleShare} disabled={!showShare}>
        <i className="fa-light fa-share mr-2" />
        <span>Share</span>
      </DropdownMenuItem>

      {/* Copy to */}
      <DropdownMenuItem onSelect={(e) => e.preventDefault()} onClick={handleCopyTo} disabled={!showCopyTo}>
        <i className="fa-light fa-copy mr-2" />
        <span>Copy to</span>
      </DropdownMenuItem>

      {/* Separator before Archive/Delete */}
      <DropdownMenuSeparator />

      {/* Archive/Unarchive */}
      <DropdownMenuItem onClick={handleArchive} disabled={!showArchive}>
        <i className="fa-light fa-folder-open mr-2" />
        <span>{record?.archived ? 'Unarchive' : 'Archive'}</span>
      </DropdownMenuItem>

      {/* Delete */}
      <DropdownMenuItem onClick={handleDelete} className="text-destructive" disabled={!showDelete}>
        <i className="fa-light fa-trash mr-2" />
        <span>Delete</span>
      </DropdownMenuItem>
    </>
  );
};

export default ClientContextMenu;